import React, { useState, useEffect } from 'react';
import apiClient, { getApiUrl } from '../../utils/apiClient';
import { 
  Settings, 
  Save, 
  Plus, 
  Trash2, 
  Award, 
  Calendar,
  Clock,
  Download,
  Upload,
  AlertCircle,
  Check,
  X,
  TrendingUp,
  Gift
} from 'lucide-react';import PageContainer from '../layout/PageContainer';






interface PointSettingsData {
  points_per_yen: number;
  point_expiry_days: number;
  require_friend: boolean;
  ranks?: RankSetting[];
}

interface RankSetting {
  id?: number;
  rank_name: string;
  min_points: number;
  max_points?: number | null;
  color: string;
  icon: string;
  benefits: string;
  sort_order?: number;
  is_active?: boolean;
}


const PointSettings: React.FC = () => {
  const [settings, setSettings] = useState<PointSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'ranks'>('basic');
  const [previewAmount, setPreviewAmount] = useState(5000);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [showCurrentRanks, setShowCurrentRanks] = useState(false);

  // TODO: 将来的にfeature flagやDB設定でアンロック判定に切り替え
  const isRankUnlocked = true;

  const defaultIcons = ['🥉', '🥈', '🥇', '💎', '👑', '⭐', '🌟', '✨'];
  const defaultColors = ['#CD7F32', '#C0C0C0', '#FFD700', '#E5E4E2', '#9B59B6', '#3498DB', '#E74C3C', '#2ECC71'];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/point-settings/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('waton_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        throw new Error('設定の取得に失敗しました');
      }
    } catch (error) {
      console.error('設定取得エラー:', error);
      showMessage('error', '設定の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleBasicSettingsChange = (field: string, value: any) => {
    if (!settings) return;
    setSettings({
      ...settings,
      [field]: value
    });
  };

  const saveBasicSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const response = await fetch(`${getApiUrl()}/point-settings/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('waton_token')}`
        },
        body: JSON.stringify({
          points_per_yen: settings.points_per_yen,
          point_expiry_days: settings.point_expiry_days,
          require_friend: settings.require_friend
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        showMessage('success', '基本設定を保存しました');

        // プレビュー計算用に最新の設定を再取得
        await fetchSettings();
      } else {
        throw new Error('保存に失敗しました');
      }
    } catch (error) {
      console.error('保存エラー:', error);
      showMessage('error', '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const handleRankChange = (index: number, field: string, value: any) => {
    if (!settings?.ranks) return;
    const newRanks = [...settings.ranks];
    newRanks[index] = {
      ...newRanks[index],
      [field]: value
    };
    setSettings({
      ...settings,
      ranks: newRanks
    });
  };

  const addRank = () => {
    if (!settings) return;
    const ranksLength = settings.ranks?.length || 0;
    const newRank: RankSetting = {
      rank_name: '新しいランク',
      min_points: ranksLength > 0 ? Math.max(...settings.ranks!.map(r => r.min_points || 0)) + 100 : 0,
      max_points: null,
      color: defaultColors[ranksLength % defaultColors.length] || '#808080',
      icon: defaultIcons[ranksLength % defaultIcons.length] || '⭐',
      benefits: '',
      is_active: true
    };
    setSettings({
      ...settings,
      ranks: [...(settings.ranks || []), newRank]
    });
  };

  const removeRank = (index: number) => {
    if (!settings?.ranks) return;
    const newRanks = settings.ranks.filter((_, i) => i !== index);
    setSettings({
      ...settings,
      ranks: newRanks
    });
  };

  const saveRanks = async () => {
    if (!settings?.ranks) return;
    setSaving(true);
    try {
      const response = await fetch(`${getApiUrl()}/point-settings/ranks`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('waton_token')}`
        },
        body: JSON.stringify({ ranks: settings.ranks })
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        showMessage('success', 'ランク設定を保存しました');
      } else {
        throw new Error('保存に失敗しました');
      }
    } catch (error) {
      console.error('保存エラー:', error);
      showMessage('error', '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const addMultiplier = () => {
    const newMultiplier: MultiplierSetting = {
      name: '新しい倍率設定',
      type: 'day_of_week',
      day_of_week: 0,
      multiplier: 1.5,
      is_active: false
    };
    setSettings({
      ...settings!,
      multipliers: [...(settings?.multipliers || []), newMultiplier]
    });
  };

  const handleMultiplierChange = (index: number, field: string, value: any) => {
    if (!settings?.multipliers) return;
    const newMultipliers = [...settings.multipliers];
    newMultipliers[index] = {
      ...newMultipliers[index],
      [field]: value
    };
    
    // タイプが変更されたら関連フィールドをリセット
    if (field === 'type') {
      if (value === 'day_of_week') {
        newMultipliers[index].day_of_week = 0;
        delete newMultipliers[index].start_time;
        delete newMultipliers[index].end_time;
      } else if (value === 'time_range') {
        delete newMultipliers[index].day_of_week;
        newMultipliers[index].start_time = '18:00';
        newMultipliers[index].end_time = '20:00';
      } else {
        delete newMultipliers[index].day_of_week;
        delete newMultipliers[index].start_time;
        delete newMultipliers[index].end_time;
      }
    }
    
    setSettings({
      ...settings,
      multipliers: newMultipliers
    });
  };

  const saveMultiplier = async (multiplier: MultiplierSetting, index: number) => {
    setSaving(true);
    try {
      if (multiplier.id) {
        // 既存の倍率設定を更新（現在はAPIが未実装なので追加で代用）
        showMessage('error', '倍率設定の更新機能は準備中です');
      } else {
        // 新規追加
        const response = await fetch(`${getApiUrl()}/point-settings/multipliers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('waton_token')}`
          },
          body: JSON.stringify(multiplier)
        });

        if (response.ok) {
          const data = await response.json();
          setSettings(data.settings);
          showMessage('success', '倍率設定を追加しました');
        } else {
          throw new Error('保存に失敗しました');
        }
      }
    } catch (error) {
      console.error('保存エラー:', error);
      showMessage('error', '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const deleteMultiplier = async (id: number | undefined, index: number) => {
    if (!id) {
      // まだ保存されていない項目は単に削除
      const newMultipliers = settings?.multipliers?.filter((_, i) => i !== index) || [];
      setSettings({
        ...settings!,
        multipliers: newMultipliers
      });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${getApiUrl()}/point-settings/multipliers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('waton_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings);
        showMessage('success', '倍率設定を削除しました');
      } else {
        throw new Error('削除に失敗しました');
      }
    } catch (error) {
      console.error('削除エラー:', error);
      showMessage('error', '削除に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const calculatePreview = () => {
    // 画面上の設定値を使って即座に計算（保存前でもプレビュー可能）
    if (!settings) return;

    const points = Math.floor(previewAmount / settings.points_per_yen);

    setPreviewResult({
      amount: previewAmount,
      points_per_yen: settings.points_per_yen,
      base_points: points,
      final_points: points,
      multiplier: 1
    });
  };

  const exportSettings = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/point-settings/export`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('waton_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `point-settings-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        showMessage('success', '設定をエクスポートしました');
      }
    } catch (error) {
      console.error('エクスポートエラー:', error);
      showMessage('error', 'エクスポートに失敗しました');
    }
  };

  const importSettings = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      const response = await fetch(`${getApiUrl()}/point-settings/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('waton_token')}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        setSettings(result.settings);
        showMessage('success', '設定をインポートしました');
      } else {
        throw new Error('インポートに失敗しました');
      }
    } catch (error) {
      console.error('インポートエラー:', error);
      showMessage('error', 'インポートに失敗しました');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Settings className="h-6 w-6 text-blue-600" />
              <h1 className="text-2xl font-bold">ポイントシステム設定</h1>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={isRankUnlocked ? exportSettings : undefined}
                disabled={!isRankUnlocked}
                className={`flex items-center space-x-2 px-4 py-2 rounded ${isRankUnlocked ? 'bg-gray-600 text-white hover:bg-gray-700 cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                title={!isRankUnlocked ? 'プレミアムオプション機能です' : ''}
              >
                <Download className="h-4 w-4" />
                <span>エクスポート</span>
              </button>
              <label className={`flex items-center space-x-2 px-4 py-2 rounded ${isRankUnlocked ? 'bg-gray-600 text-white hover:bg-gray-700 cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                title={!isRankUnlocked ? 'プレミアムオプション機能です' : ''}
              >
                <Upload className="h-4 w-4" />
                <span>インポート</span>
                <input
                  type="file"
                  accept=".json"
                  onChange={isRankUnlocked ? importSettings : undefined}
                  disabled={!isRankUnlocked}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>

        {message && (
          <div className={`mx-6 mt-4 p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {message.type === 'success' ? <Check className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            <span>{message.text}</span>
          </div>
        )}

        <div className="p-6">
          <div className="flex space-x-4 mb-6 border-b">
            <button
              onClick={() => setActiveTab('basic')}
              className={`pb-2 px-4 ${activeTab === 'basic' ? 'border-b-2 border-blue-500 font-semibold' : ''}`}
            >
              基本設定
            </button>
            <button
              onClick={() => setActiveTab('ranks')}
              className={`pb-2 px-4 ${activeTab === 'ranks' ? 'border-b-2 border-blue-500 font-semibold' : ''}`}
            >
              ランク設定
            </button>

          </div>

          {activeTab === 'basic' && settings && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ポイント付与単位（円）
                  </label>
                  <input
                    type="number"
                    value={settings.points_per_yen || 500}
                    onChange={(e) => handleBasicSettingsChange('points_per_yen', parseInt(e.target.value) || 500)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {settings.points_per_yen}円ごとに1ポイント付与
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ポイント有効期限（日）
                  </label>
                  <input
                    type="number"
                    value={settings.point_expiry_days || 3}
                    onChange={(e) => handleBasicSettingsChange('point_expiry_days', parseInt(e.target.value) || 3)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    レシート発行から{settings.point_expiry_days}日間有効
                  </p>
                </div>



              </div>

              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="require_friend"
                    checked={settings.require_friend}
                    onChange={(e) => handleBasicSettingsChange('require_friend', e.target.checked)}
                    className="h-4 w-4 text-blue-600"
                  />
                  <label htmlFor="require_friend" className="text-sm font-medium text-gray-700">
                    LINE友だち登録が必要
                  </label>
                </div>

              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-3">ポイント計算プレビュー</h3>
                <div className="flex items-center space-x-4">
                  <input
                    type="number"
                    value={previewAmount}
                    onChange={(e) => setPreviewAmount(parseInt(e.target.value) || 5000)}
                    className="px-3 py-2 border rounded-lg"
                    placeholder="金額"
                  />
                  <button
                    onClick={calculatePreview}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    計算
                  </button>
                  {previewResult && (
                    <div className="flex-1">
                      <span className="text-lg">
                        {previewAmount.toLocaleString()}円 → 
                        <span className="font-bold text-blue-600 ml-2">
                          {previewResult.final_points}ポイント
                        </span>
                        {previewResult.multiplier > 1 && (
                          <span className="text-sm text-gray-500 ml-2">
                            (基本{previewResult.base_points}pt × {previewResult.multiplier}倍)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={saveBasicSettings}
                  disabled={saving}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  <span>{saving ? '保存中...' : '基本設定を保存'}</span>
                </button>
              </div>
            </div>
          )}

          {activeTab === 'ranks' && settings && (
            !isRankUnlocked ? (
              <div>
                {/* プレミアムオプション案内 */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: 16,
                  padding: '40px 32px',
                  color: '#fff',
                  textAlign: 'center',
                  marginBottom: 24,
                  boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)'
                }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>&#x1F451;</div>
                  <h2 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px 0', letterSpacing: 1 }}>
                    プレミアムオプション
                  </h2>
                  <h3 style={{ fontSize: 18, fontWeight: 400, margin: '0 0 24px 0', opacity: 0.9 }}>
                    ランク設定管理
                  </h3>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: 12,
                    maxWidth: 700,
                    margin: '0 auto 24px',
                    textAlign: 'left'
                  }}>
                    {[
                      { icon: '\u{1F3C5}', title: 'ランク追加・編集', desc: 'ランク名・ポイント範囲・アイコン・カラーを自由に設定' },
                      { icon: '\u{1F381}', title: '特典設定', desc: 'ランクごとの特典内容をカスタマイズ' },
                      { icon: '\u{1F5D1}\u{FE0F}', title: 'ランク削除', desc: '不要なランクの削除に対応' },
                      { icon: '\u{1F4CA}', title: 'リアルタイム反映', desc: '設定変更が会員カードに即時反映' },
                    ].map((feature, i) => (
                      <div key={i} style={{
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: 12,
                        padding: '14px 18px',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <div style={{ fontSize: 22, marginBottom: 4 }}>{feature.icon}</div>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>{feature.title}</div>
                        <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.5 }}>{feature.desc}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{
                    display: 'inline-block',
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: 8,
                    padding: '10px 24px',
                    fontSize: 14,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                  }}>
                    &#x1F6A7; 開発検討中 &#x2015; ご契約・お問い合わせは開発担当まで
                  </div>
                </div>

                {/* 検収前初期設定期間（緑枠） */}
                <div style={{
                  background: '#f6ffed',
                  border: '2px solid #52c41a',
                  borderRadius: 8,
                  padding: '16px 24px',
                  marginBottom: 24,
                  maxWidth: 500
                }}>
                  <p style={{ fontWeight: 700, color: '#389e0d', marginBottom: 8, fontSize: 14 }}>
                    &#x1F4C5; 検収前初期設定期間
                  </p>
                  <p style={{ color: '#595959', fontSize: 13, margin: 0, lineHeight: 1.8 }}>
                    <strong>2月25日まで</strong>：検収前初期設定期間につき、ランク設定の変更は<strong style={{ color: '#389e0d' }}>無償</strong>にて対応いたします。<br />
                    <strong>2月26日以降</strong>：別途お見積りさせていただきます。
                  </p>
                </div>

                {/* 現在の設定値を見るアコーディオン */}
                <div style={{ marginBottom: 24 }}>
                  <button
                    onClick={() => setShowCurrentRanks(!showCurrentRanks)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      background: '#f5f5f5',
                      border: '1px solid #d9d9d9',
                      borderRadius: 8,
                      padding: '10px 20px',
                      cursor: 'pointer',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#333',
                      width: '100%',
                      textAlign: 'left'
                    }}
                  >
                    <span style={{
                      display: 'inline-block',
                      transition: 'transform 0.2s',
                      transform: showCurrentRanks ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}>&#x25B6;</span>
                    現在の設定値を見る（読み取り専用）
                  </button>

                  {showCurrentRanks && (
                    <div style={{
                      border: '1px solid #d9d9d9',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      padding: 16,
                      background: '#fafafa'
                    }}>
                      {settings.ranks && settings.ranks.length > 0 ? (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e8e8e8' }}>
                              <th style={{ padding: '8px 12px', textAlign: 'left', color: '#666' }}>アイコン</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', color: '#666' }}>ランク名</th>
                              <th style={{ padding: '8px 12px', textAlign: 'right', color: '#666' }}>最小pt</th>
                              <th style={{ padding: '8px 12px', textAlign: 'right', color: '#666' }}>最大pt</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', color: '#666' }}>カラー</th>
                              <th style={{ padding: '8px 12px', textAlign: 'left', color: '#666' }}>特典</th>
                            </tr>
                          </thead>
                          <tbody>
                            {settings.ranks.map((rank, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ padding: '10px 12px', fontSize: 20 }}>{rank.icon}</td>
                                <td style={{ padding: '10px 12px', fontWeight: 600 }}>{rank.rank_name}</td>
                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>{rank.min_points.toLocaleString()}</td>
                                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                  {rank.max_points != null ? rank.max_points.toLocaleString() : '無制限'}
                                </td>
                                <td style={{ padding: '10px 12px' }}>
                                  <span style={{
                                    display: 'inline-block',
                                    width: 20,
                                    height: 20,
                                    borderRadius: 4,
                                    backgroundColor: rank.color,
                                    verticalAlign: 'middle',
                                    border: '1px solid #ddd'
                                  }} />
                                </td>
                                <td style={{ padding: '10px 12px', color: '#666' }}>{rank.benefits || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p style={{ color: '#999', textAlign: 'center', margin: '16px 0' }}>
                          ランク設定がまだありません
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* ぼかしプレビュー */}
                <div style={{ position: 'relative' }}>
                  <div style={{ filter: 'blur(4px)', opacity: 0.5, pointerEvents: 'none', userSelect: 'none' }}>
                    <div className="space-y-4">
                      {(settings.ranks || []).slice(0, 2).map((rank, index) => (
                        <div key={index} className="border rounded-lg p-4">
                          <div className="grid grid-cols-6 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">ランク名</label>
                              <input type="text" value={rank.rank_name} disabled className="w-full px-3 py-2 border rounded-lg bg-gray-100" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">最小ポイント</label>
                              <input type="number" value={rank.min_points} disabled className="w-full px-3 py-2 border rounded-lg bg-gray-100" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">最大ポイント</label>
                              <input type="number" disabled className="w-full px-3 py-2 border rounded-lg bg-gray-100" placeholder="無制限" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    zIndex: 10,
                    color: '#8c8c8c',
                    fontSize: 14
                  }}>
                    &#x1F512; 編集にはプレミアムオプションが必要です
                  </div>
                </div>
              </div>
            ) : (
            <div className="space-y-6">
              <div className="space-y-4">
                {settings.ranks?.map((rank, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="grid grid-cols-6 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ランク名
                        </label>
                        <input
                          type="text"
                          value={rank.rank_name}
                          onChange={(e) => handleRankChange(index, 'rank_name', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          最小ポイント
                        </label>
                        <input
                          type="number"
                          value={rank.min_points}
                          onChange={(e) => handleRankChange(index, 'min_points', parseInt(e.target.value) || 0)}
                          className="w-full px-3 py-2 border rounded-lg"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          最大ポイント
                        </label>
                        <input
                          type="number"
                          value={rank.max_points !== null && rank.max_points !== undefined ? rank.max_points : ''}
                          onChange={(e) => handleRankChange(index, 'max_points', e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="無制限"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          アイコン
                        </label>
                        <select
                          value={rank.icon}
                          onChange={(e) => handleRankChange(index, 'icon', e.target.value)}
                          className="w-full px-3 py-2 border rounded-lg"
                        >
                          {defaultIcons.map(icon => (
                            <option key={icon} value={icon}>{icon}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          カラー
                        </label>
                        <input
                          type="color"
                          value={rank.color}
                          onChange={(e) => handleRankChange(index, 'color', e.target.value)}
                          className="w-full h-10 border rounded-lg cursor-pointer"
                        />
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={() => removeRank(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        特典
                      </label>
                      <input
                        type="text"
                        value={rank.benefits}
                        onChange={(e) => handleRankChange(index, 'benefits', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg"
                        placeholder="例: 誕生月ポイント2倍、月1回クーポン"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={addRank}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="h-4 w-4" />
                  <span>ランクを追加</span>
                </button>
                <button
                  onClick={saveRanks}
                  disabled={saving}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  <span>{saving ? '保存中...' : 'ランク設定を保存'}</span>
                </button>
              </div>
            </div>
            )
          )}

        </div>
      </div>
    </div>
  );
};

export default PointSettings;